import { useState, useEffect } from "react";
import "./admin-courses-creation.css";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../../ui/atoms/buttons/Button";
import InputText from "../../../../ui/atoms/inputs/input-text/InputText";
import InputSelect from "../../../../ui/atoms/inputs/input-select/InputSelect";
import NotFound from "../../../../ui/organisms/not-found/404";
import CustomizedTextEditor from "../../../../ui/organisms/customized-text-editor/CustomizedTextEditor";
import { PiFileAudioBold, PiVideoBold } from "react-icons/pi";
import { IoMdImages } from "react-icons/io";
import { SlClose } from "react-icons/sl";
//
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
//
import { LessonSchema } from "../../../../core/utils/validators/course";
import {
  wait,
  convertDuration,
  capitalizeAll,
  isEmpty,
} from "../../../../core/utils/helpers";
import {
  onHandleSingleFile,
  onHandleMultipleFiles,
} from "../../../../core/utils/common-validators";
import { onCreateLesson } from "../../services/course";
import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";
//
import Swal from "sweetalert2";
import { useSelector } from "react-redux";

const AdminCourseChapterLessonsCreation = () => {
  const axiosPrivate = useAxiosPrivate();
  const { chapter_id } = useParams();
  const navigate = useNavigate();
  const [isSending, setIsSending] = useState(false);
  const [isNewSection, setIsSNewSection] = useState(false);

  const [timing, setTiming] = useState(60);
  const [audioFiles, setAudioFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  const [lessonSectionContent, setLessonSectionContent] = useState([]);
  const [description, setDescription] = useState("");

  const currentChapter = useSelector(
    (state) => state?.schoolState?.currentChapter,
  );

  const _chapter_id = currentChapter?.id;
  const _chapter_title = currentChapter?.title;

  useEffect(() => {
    if (chapter_id !== _chapter_id) {
      return <NotFound type={"page"} />;
    }
  }, [chapter_id]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(LessonSchema),
    defaultValues: {
      chapters_id: _chapter_id,
    },
  });

  const onSubmit = async (data) => {
    setIsSending(true);
    Swal.fire({
      title: "Creating new chapter's lesson...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    await wait(700);
    //
    let formData = new FormData();
    //
    formData.append("chapters_id", data?.chapters_id);
    formData.append("language", data?.language);
    formData.append("title", data?.title);
    formData.append("types", data?.type);
    formData.append("accessibility", data?.accessibility);
    formData.append("timing", timing);

    lessonSectionContent.forEach((section, sectionIndex) => {
      formData.append(
        `section_${sectionIndex}_description`,
        section.description,
      );

      section.audios?.forEach((file, fileIndex) => {
        formData.append(`section_${sectionIndex}_audio_${fileIndex}`, file);
      });

      section.images?.forEach((file, fileIndex) => {
        formData.append(`section_${sectionIndex}_image_${fileIndex}`, file);
      });

      section.videos?.forEach((file, fileIndex) => {
        formData.append(`section_${sectionIndex}_video_${fileIndex}`, file);
      });
    });

    onCreateLesson(axiosPrivate, formData)
      .then((response) => {
        const _data = response?.data;
        Swal.close();
        setIsSending(false);
        if (_data?.status) {
          Swal.fire({
            title: "Chapter's Lesson creation",
            text: _data?.message,
            icon: "success",
            confirmButtonText: "OK",
            didOpen: () => {
              const btn = Swal.getConfirmButton();
              btn.style.backgroundColor = "#2840a8";
              btn.style.color = "white";
            },
          });
          reset();
          setDescription("");
          setTiming(60);
          setAudioFiles([]);
          setVideoFiles([]);
          setImageFiles([]);
          setLessonSectionContent([]);
        } else {
          Swal.fire({
            title: "Chapter's Lesson creation",
            text: _data?.message,
            icon: "info",
          });
        }
      })
      .catch((error) => {
        setIsSending(false);
        Swal.close();
        if (!error?.response) {
          Swal.fire({
            title: "Chapter's Lesson creation",
            text: "No server response",
            icon: "warning",
          });
        } else {
          Swal.fire({
            title: "Chapter's Lesson creation",
            text: error?.response?.data?.detail?.message,
            icon: "error",
          });
        }
      });
  };

  const removeFile = (type, idx) => {
    if (type === "audio") {
      setAudioFiles((prev) => prev.filter((_, i) => i !== idx));
    }

    if (type === "video") {
      setVideoFiles((prev) => prev.filter((_, i) => i !== idx));
    }

    if (type === "image") {
      setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  const onAddLessonSectionContent = () => {
    setLessonSectionContent((prevState) => [
      ...prevState,
      {
        description: description,
        audios: audioFiles,
        videos: videoFiles,
        images: imageFiles,
      },
    ]);
    Swal.fire({
      title: "Lesson Section creation",
      text: "Section content added!",
      icon: "success",
      confirmButtonText: "OK",
    });
    setDescription("");
  };

  return (
    <form className="lessons-creation" onSubmit={handleSubmit(onSubmit)}>
      <div className="w-full">
        <div className="lesson-cr-head">
          <button type="button" onClick={() => navigate(-1)}>
            ⬅️ Back to list
          </button>
          <h3>Chapter : {capitalizeAll(_chapter_title)}</h3>
          <div className="w-full d-flex justify-space-between align-center">
            <h1>📖 New Lesson Creation</h1>
            <Button
              type={"button"}
              preferedStyle={"default"}
              title={"Adding a new lesson section"}
              action={() => setIsSNewSection(true)}
            />
            <Button
              type={isSending ? "button" : "submit"}
              preferedStyle={isSending ? "default" : "normal"}
              title={
                isSending
                  ? "Creating new lesson..."
                  : "Validate and Create new lesson"
              }
            />
          </div>
        </div>
        <div className="lesson-cr-body">
          <div className="w-full d-flex gap-md mb-lg">
            <div className="w-50">
              <InputSelect
                name={"language"}
                error={errors?.language?.message}
                register={register}
                data={[
                  { key: "en", title: "English", is_activated: false },
                  { key: "fr", title: "French", is_activated: false },
                ]}
                placeholder={"Select language"}
              />
            </div>
            <div className="w-50">
              <InputText
                type={"text"}
                name={"title"}
                title={"Title"}
                error={errors?.title?.message}
                register={register}
              />
            </div>
          </div>
          <div className="w-full d-flex gap-md mb-lg">
            <div className="w-50">
              <InputSelect
                name={"type"}
                error={errors?.type?.message}
                register={register}
                data={[
                  { key: "lesson", title: "Lesson", is_activated: false },
                  {
                    key: "exercise",
                    title: "Exercise",
                    is_activated: false,
                  },
                  {
                    key: "assessment",
                    title: "Assessment",
                    is_activated: false,
                  },
                  {
                    key: "laboratory",
                    title: "Laboratory",
                    is_activated: false,
                  },
                ]}
                placeholder={"Select content type"}
              />
            </div>
            <div className="w-50">
              <InputSelect
                name={"accessibility"}
                error={errors?.accessibility?.message}
                register={register}
                data={[
                  { key: "fremium", title: "Fremium", is_activated: false },
                  { key: "premium", title: "Premium", is_activated: false },
                ]}
                placeholder={"Select Accessibility"}
              />
            </div>
          </div>
          <div className="w-full d-flex gap-md mb-lg">
            <div className="w-full timing-wrapper">
              <span>Lesson Timing</span>
              <div className="w-full d-flex align-center gap-xs">
                <input
                  className="w-80"
                  type="range"
                  name="timing"
                  id="timing"
                  min={60}
                  max={5400}
                  step={1}
                  value={timing}
                  onChange={(e) => setTiming(e.target.value)}
                />
                <output htmlFor="timing">{convertDuration(timing)}</output>
              </div>
            </div>
          </div>
          <div className="w-full lesson-cr-body-assets">
            {isEmpty(lessonSectionContent) ? (
              <p
                style={{
                  textAlign: "center",
                  fontStyle: "italic",
                  color: "red",
                }}
              >
                Waiting for content to be setted up...
              </p>
            ) : (
              lessonSectionContent.map((row, idx) => {
                return (
                  <>
                    <div className="lesson-cr-body-audios">
                      {row?.audios?.map((audio, a_k) => {
                        return (
                          <div className="body-audios-item" key={a_k}>
                            <audio
                              controls
                              controlsList="nodownload"
                              onContextMenu={(e) => e.preventDefault()}
                            >
                              <source
                                src={URL.createObjectURL(audio)}
                                type={audio?.type}
                              />
                            </audio>
                          </div>
                        );
                      })}
                    </div>
                    <div className="lesson-cr-body-videos">
                      {row?.videos?.map((video, v_k) => {
                        return (
                          <div className="body-videos-item" key={v_k}>
                            <video
                              controls
                              width={"100%"}
                              controlsList="nodownload"
                              onContextMenu={(e) => e.preventDefault()}
                            >
                              <source
                                src={URL.createObjectURL(video)}
                                type={video?.type}
                              />
                              Your browser does not support the video element.
                            </video>
                          </div>
                        );
                      })}
                    </div>
                    <div className="lesson-cr-body-images">
                      {row?.images?.map((image, i_k) => {
                        return (
                          <div className="body-images-item" key={i_k}>
                            <img
                              src={URL.createObjectURL(image)}
                              alt=""
                              controlsList="nodownload"
                              onContextMenu={(e) => e.preventDefault()}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <p
                      key={idx}
                      dangerouslySetInnerHTML={{
                        __html: row?.description,
                      }}
                    />
                  </>
                );
              })
            )}
          </div>
        </div>
      </div>
      <div
        className={
          isNewSection
            ? "w-full lesson-section slide-in"
            : "w-full lesson-section slide-out"
        }
      >
        <div className="lesson-section-container">
          <div className="lesson-section-head">
            <h1>Editing new lesson section</h1>
            {!isEmpty(description) && (
              <Button
                type={"button"}
                preferedStyle={"normal"}
                title={"Validate content"}
                action={() => onAddLessonSectionContent()}
              />
            )}
            <Button
              type={"button"}
              preferedStyle={"default"}
              title={"Close"}
              action={() => setIsSNewSection(false)}
            />
          </div>
          <div className="lesson-section-body">
            <div className="section-content">
              <CustomizedTextEditor
                content={description}
                setContent={setDescription}
              />
            </div>
            <div className="section-assets">
              <div className="w-full">
                <div className="browse-image">
                  <input
                    type="file"
                    id="audio_input"
                    accept="audio/mpeg,audio/wav,audio/mp3"
                    multiple
                    onChange={(e) => {
                      const audioFiles = onHandleMultipleFiles(e, "audio");
                      if (audioFiles.length === 0) return;

                      setAudioFiles(audioFiles);
                    }}
                  />
                  <label htmlFor="audio_input">
                    <PiFileAudioBold className="icon" />
                    <span>Load Audios</span>
                  </label>
                </div>
                {audioFiles.length > 0 && (
                  <div className="files-container">
                    {audioFiles.map((audioFile, idx) => {
                      return (
                        <div className="file-item file-item-audio" key={idx}>
                          <span className="file-icon">
                            <PiFileAudioBold className="icon" />
                          </span>
                          <div className="file-description">
                            <audio
                              className="w-full"
                              controls
                              controlsList="nodownload"
                              onContextMenu={(e) => e.preventDefault()}
                            >
                              <source
                                src={URL.createObjectURL(audioFile)}
                                type={audioFile?.type}
                              />
                            </audio>
                          </div>
                          <span
                            className="file-action"
                            onClick={() => removeFile("audio", idx)}
                          >
                            <SlClose className="icon" />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="w-full">
                <div className="browse-image">
                  <input
                    type="file"
                    id="video_input"
                    accept="video/mp4,video/webm"
                    multiple
                    onChange={(e) => {
                      const videoFiles = onHandleMultipleFiles(e, "video");
                      if (videoFiles.length === 0) return;

                      setVideoFiles(videoFiles);
                    }}
                  />
                  <label htmlFor="video_input">
                    <PiVideoBold className="icon" />
                    <span>Load Video</span>
                  </label>
                </div>
                {videoFiles.length > 0 && (
                  <div className="files-container">
                    {videoFiles.map((videoFile, idx) => {
                      return (
                        <div className="file-item file-item-video" key={idx}>
                          <div className="file-description">
                            <video
                              controls
                              width={500}
                              controlsList="nodownload"
                              onContextMenu={(e) => e.preventDefault()}
                            >
                              <source
                                src={URL.createObjectURL(videoFile)}
                                type={videoFile?.type}
                              />
                              Your browser does not support the video element.
                            </video>
                          </div>
                          <span
                            className="file-action"
                            onClick={() => removeFile("video", idx)}
                          >
                            <SlClose className="icon" />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="w-full">
                <div className="browse-image">
                  <input
                    type="file"
                    id="image_input"
                    accept="image/jpg,image/jpeg,image/png"
                    multiple
                    onChange={(e) => {
                      const imageFiles = onHandleMultipleFiles(e, "image");
                      if (imageFiles.length === 0) return;

                      setImageFiles(imageFiles);
                    }}
                  />
                  <label htmlFor="image_input">
                    <IoMdImages className="icon" />
                    <span>Load Images</span>
                  </label>
                </div>
                {imageFiles.length > 0 && (
                  <div className="files-container">
                    {imageFiles.map((imageFile, idx) => {
                      return (
                        <div className="file-item-img" key={idx}>
                          <div className="file-description">
                            <img
                              src={URL.createObjectURL(imageFile)}
                              alt=""
                              controlsList="nodownload"
                              onContextMenu={(e) => e.preventDefault()}
                            />
                          </div>
                          <span
                            className="file-action"
                            onClick={() => removeFile("image", idx)}
                          >
                            <SlClose className="icon" />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AdminCourseChapterLessonsCreation;
